import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import './chat-widget.css';
import { getOrCreateDialogCode, readSessionId, watchSessionId } from '../utils/storage';
import { createChatConnection, mapMessage } from '../services/chat-service';
import iconMax from '../assets/icon-max.png';
import iconVk from '../assets/icon-vk.png';
import { ReactComponent as IconSend } from '../assets/icon-send.svg';

const MESSENGER_ICONS = {
  3: iconMax,
  5: iconVk
};

const MESSENGER_LABELS = {
  1: 'TELEGRAM',
  3: 'MAX',
  5: 'ВКОНТАКТЕ'
};

function getMessengerLabel(messenger) {
  const vid = Number(messenger.vid);
  if (MESSENGER_LABELS[vid]) {
    return MESSENGER_LABELS[vid];
  }
  const name = String(messenger.name || '').trim();
  return name ? name.toUpperCase() : 'МЕССЕНДЖЕР';
}

function getMessengerIcon(messenger) {
  return MESSENGER_ICONS[Number(messenger.vid)] || null;
}

const ChatWidget = ({ config = {} }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [nameValue, setNameValue] = useState('');
  const [messengers, setMessengers] = useState([]);
  const [needUserInfo, setNeedUserInfo] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected'); // disconnected | connecting | connected | reconnecting | error
  const [errorText, setErrorText] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const nameInputRef = useRef(null);
  const chatRef = useRef(null);
  const lastIdentifiedSessionRef = useRef(null);

  const {
    tokenCode,
    chatHubUrl: configHubUrl
  } = config;

  const defaultHubUrl = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_CHAT_HUB_URL || 'https://localhost:7047';
  const hubBaseUrl = (configHubUrl && typeof configHubUrl === 'string') ? configHubUrl : defaultHubUrl;

  const code = tokenCode ? getOrCreateDialogCode(tokenCode) : null;

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (isOpen && needUserInfo && nameInputRef.current) {
      nameInputRef.current.focus();
      return;
    }
    if (isOpen && !needUserInfo && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, needUserInfo]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleToggle = useCallback(() => {
    if (isOpen && chatRef.current) {
      chatRef.current.stop().catch(() => {});
      chatRef.current = null;
      setConnectionStatus('disconnected');
      setErrorText('');
      setMessengers([]);
      setNeedUserInfo(null);
      setNameValue('');
      lastIdentifiedSessionRef.current = null;
    }
    setIsOpen(!isOpen);
  }, [isOpen]);

  // При открытии чата — подключаемся к SignalR и загружаем историю
  useEffect(() => {
    if (!isOpen || !tokenCode || !code) return;

    setConnectionStatus('connecting');
    setErrorText('');
    setMessengers([]);
    setNeedUserInfo(null);

    const chat = createChatConnection(
      hubBaseUrl,
      tokenCode,
      code,
      {
        onReceiveMessage: (msg) => {
          setMessages((prev) => {
            if (prev.some((m) => m.id === msg.id)) return prev;
            return [...prev, msg];
          });
          setErrorText('');
        },
        onError: (text) => {
          setErrorText(text || 'Ошибка чата');
          setConnectionStatus('error');
        },
        onConnectionStateChange: (state) => {
          if (state === 'connected' || state === 'reconnecting') {
            setConnectionStatus(state);
            setErrorText('');
          } else if (state === 'closed') {
            setConnectionStatus('disconnected');
          }
        },
        onRequestUserInfo: (payload) => {
          if (payload) {
            setNeedUserInfo(payload);
            setNameValue('');
          }
        }
      }
    );

    chatRef.current = chat;

    (async () => {
      try {
        await chat.start();
        const result = await chat.join();
        const rawMessages = (result && (result.messages || result.Messages)) || [];
        if (Array.isArray(rawMessages)) {
          setMessages(rawMessages.map(mapMessage));
        }
        setMessengers(result.messengers || []);
        setNeedUserInfo(result.needUserInfo || null);

        const sessionId = readSessionId();
        if (sessionId) {
          lastIdentifiedSessionRef.current = sessionId;
          const identified = await chat.identify(sessionId);
          if (identified && Object.prototype.hasOwnProperty.call(identified, 'needUserInfo')) {
            setNeedUserInfo(identified.needUserInfo || null);
          }
        }

        setConnectionStatus('connected');
      } catch (err) {
        const message = err && (err.message || err.toString());
        setErrorText(message || 'Не удалось подключиться к чату');
        setConnectionStatus('error');
      }
    })();

    return () => {
      chat.stop().catch(() => {});
      chatRef.current = null;
    };
  }, [isOpen, tokenCode, code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen || connectionStatus !== 'connected') return undefined;
    const unwatch = watchSessionId((sessionId) => {
      if (!chatRef.current || lastIdentifiedSessionRef.current === sessionId) return;
      lastIdentifiedSessionRef.current = sessionId;
      chatRef.current.identify(sessionId).then((result) => {
        if (result && Object.prototype.hasOwnProperty.call(result, 'needUserInfo')) {
          setNeedUserInfo(result.needUserInfo || null);
        }
      }).catch(() => {});
    });
    return unwatch;
  }, [isOpen, connectionStatus]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (needUserInfo) return;
    const text = inputValue.trim();
    if (!text || !chatRef.current) return;

    const optimisticId = `opt-${Date.now()}`;
    const optimistic = {
      id: optimisticId,
      text,
      sender: 'user',
      direction: 1,
      timestamp: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
    };
    setMessages((prev) => [...prev, optimistic]);
    setInputValue('');

    try {
      await chatRef.current.sendMessage(text);
      setErrorText('');
      setMessages((prev) =>
        prev.map((m) => (m.id === optimisticId ? { ...m, id: `sent-${Date.now()}` } : m))
      );
    } catch (err) {
      const message = err && (err.message || err.toString());
      setErrorText(message || 'Ошибка отправки');
      setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
    }
  };

  const handleSubmitName = async (e) => {
    e.preventDefault();
    const name = nameValue.trim();
    if (!name || !chatRef.current) return;
    try {
      await chatRef.current.submitUserInfo(name);
      setNeedUserInfo(null);
      setNameValue('');
      setErrorText('');
    } catch (err) {
      const message = err && (err.message || err.toString());
      setErrorText(message || 'Не удалось сохранить имя');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(e);
    }
  };

  const statusLabel =
    connectionStatus === 'connecting'
      ? 'Подключение...'
      : connectionStatus === 'reconnecting'
        ? 'Переподключение...'
        : connectionStatus === 'error'
          ? 'Ошибка'
          : connectionStatus === 'connected'
            ? 'Онлайн'
            : 'Офлайн';

  const canSend = tokenCode && chatRef.current && connectionStatus === 'connected' && !needUserInfo && inputValue.trim() !== '';
  const showTokenError = isOpen && !tokenCode;
  const messageInputDisabled = !tokenCode || connectionStatus !== 'connected' || !!needUserInfo;

  return (
    <div className="misoft-chat-widget">
      {isOpen && (
        <div className="misoft-chat-container">
          <div className="misoft-chat-header">
            <div className="misoft-chat-header-content">
              <div className={`misoft-chat-status misoft-chat-status-${connectionStatus}`}>
                <span className="misoft-status-indicator" />
                <span className="misoft-status-text">{statusLabel}</span>
              </div>
              <h3>Диалог с оператором</h3>
            </div>
            <button
              type="button"
              className="misoft-chat-close"
              onClick={handleToggle}
              aria-label="Закрыть чат"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {errorText && (
            <div className="misoft-chat-error" role="alert">
              {errorText}
            </div>
          )}

          {showTokenError && (
            <div className="misoft-chat-error">
              Укажите tokenCode при добавлении виджета на страницу.
            </div>
          )}

          <div className="misoft-chat-messages">
            {messages.length === 0 && !showTokenError ? (
              <div className="misoft-chat-welcome">
                <div className="misoft-welcome-icon">💬</div>
                <h4>Здравствуйте!</h4>
                <p>
                  {connectionStatus === 'connecting' || connectionStatus === 'reconnecting'
                    ? 'Загрузка истории...'
                    : 'Напишите нам, и мы ответим в ближайшее время'}
                </p>
              </div>
            ) : (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`misoft-message misoft-message-direction-${message.direction === 1 ? '1' : 'n1'}`}
                >
                  <div className="misoft-message-content">
                    <div className="misoft-message-markdown">
                      <ReactMarkdown>{message.text || ''}</ReactMarkdown>
                    </div>
                    <span className="misoft-message-time">{message.timestamp}</span>
                  </div>
                </div>
              ))
            )}
            <div ref={messagesEndRef} />
          </div>

          {needUserInfo && (
            <form className="misoft-chat-name-form" onSubmit={handleSubmitName}>
              <p className="misoft-chat-name-prompt">{needUserInfo.prompt}</p>
              <div className="misoft-chat-name-row">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="misoft-chat-name-input"
                  value={nameValue}
                  onChange={(e) => setNameValue(e.target.value)}
                  placeholder="Ваше имя"
                  maxLength={200}
                  autoComplete="name"
                />
                <button
                  type="submit"
                  className="misoft-chat-name-submit"
                  disabled={!nameValue.trim() || connectionStatus !== 'connected'}
                >
                  Отправить
                </button>
              </div>
            </form>
          )}

          <div className="misoft-chat-bottom">
            {messengers.length > 0 && (
              <div className="misoft-chat-messengers">
                {messengers.map((m) => {
                  const icon = getMessengerIcon(m);
                  return (
                    <a
                      key={`${m.vid}-${m.url}`}
                      className="misoft-chat-messenger-button"
                      href={m.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {icon ? (
                        <span className="misoft-chat-messenger-icon">
                          <img src={icon} alt="" width={24} height={24} />
                        </span>
                      ) : null}
                      <span>{getMessengerLabel(m)}</span>
                    </a>
                  );
                })}
              </div>
            )}

            <form className="misoft-chat-input-form" onSubmit={handleSendMessage}>
              <div className="misoft-chat-input-container">
                <textarea
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={
                    !tokenCode
                      ? 'Укажите tokenCode для чата'
                      : needUserInfo
                        ? 'Сначала укажите имя'
                        : 'Введите сообщение...'
                  }
                  rows="1"
                  className="misoft-chat-input"
                  disabled={messageInputDisabled}
                />
                <button
                  type="submit"
                  className="misoft-chat-send-button"
                  disabled={!canSend}
                  aria-label="Отправить сообщение"
                >
                  <span className="misoft-chat-send-icon">
                    <IconSend width={18} height={18} aria-hidden="true" />
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <button
        type="button"
        className={`misoft-chat-toggle ${isOpen ? 'misoft-chat-toggle-open' : ''}`}
        onClick={handleToggle}
        aria-label={isOpen ? 'Закрыть чат' : 'Открыть чат'}
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="misoft-chat-toggle-text">Диалог с оператором</span>
          </>
        )}
      </button>
    </div>
  );
};

export default ChatWidget;
